import {
  resumeUpload,
  UploadClientError,
  type UploadClientOptions,
  type UploadSource,
  uploadAsset,
} from "@wirevod/uploader";
import { SpeedMeter } from "./speed";
import { forget, loadPersisted, matchFile, type PersistedUpload, persist } from "./store";

export type UploadStatus =
  | "queued"
  | "uploading"
  | "paused"
  | "interrupted"
  | "done"
  | "failed"
  | "canceled";

export interface UploadItem {
  key: string;
  title: string;
  fileName: string;
  size: number;
  drm: boolean;
  status: UploadStatus;
  uploadedBytes: number;
  /** Bytes per second, when known. */
  rate: number | null;
  uploadId?: string;
  assetId?: string;
  error?: string;
}

export interface UploadDeps {
  /** Current access token (refreshing first if needed). */
  getToken: () => Promise<string>;
  /** Cancels the server-side session for a canceled upload. */
  abortRemote: (uploadId: string) => Promise<void>;
  /** Called when an upload finishes, e.g. to refresh asset lists. */
  onComplete: (item: UploadItem) => void;
  fetch?: typeof fetch;
  concurrency?: number;
}

interface Internal {
  file?: File;
  controller?: AbortController;
  meter: SpeedMeter;
}

const PARALLEL_FILES = 2;
/** Finished rows leave the panel on their own after this long. */
const DONE_LINGER_MS = 12_000;

export function fileSource(file: File): UploadSource {
  return { size: file.size, slice: (start, end) => file.slice(start, end).arrayBuffer() };
}

function describe(error: unknown): string {
  if (error instanceof UploadClientError) {
    if (error.code === "quota_exceeded" || error.status === 402) {
      return "Your plan's processing allowance is used up.";
    }
    if (error.code === "upload_not_pending") return "This upload expired. Start it again.";
    if (error.status === 401) return "Your session ended. Sign in again to continue.";
  }
  return "The upload failed. Check your connection and try again.";
}

/**
 * Runs uploads in the background, two files at a time, with pause, resume,
 * cancel and resume-after-reload. Framework-free; React subscribes to it.
 */
export class UploadManager {
  private items: UploadItem[] = [];
  private readonly internal = new Map<string, Internal>();
  private readonly listeners = new Set<() => void>();
  private nextKey = 1;

  constructor(private readonly deps: UploadDeps) {
    for (const record of loadPersisted()) this.addInterrupted(record);
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): UploadItem[] => this.items;

  /** True while bytes are moving or waiting to move. */
  get busy(): boolean {
    return this.items.some((i) => i.status === "uploading" || i.status === "queued");
  }

  add(files: Array<{ file: File; title: string; drm: boolean }>): void {
    for (const { file, title, drm } of files) {
      const key = String(this.nextKey++);
      this.internal.set(key, { file, meter: new SpeedMeter() });
      this.items = [
        ...this.items,
        {
          key,
          title: title.trim() || file.name,
          fileName: file.name,
          size: file.size,
          drm,
          status: "queued",
          uploadedBytes: 0,
          rate: null,
        },
      ];
    }
    this.emit();
    this.pump();
  }

  pause(key: string): void {
    const item = this.find(key);
    if (item?.status !== "uploading") return;
    this.internal.get(key)?.controller?.abort();
    this.update(key, { status: "paused", rate: null });
  }

  resume(key: string): void {
    const item = this.find(key);
    if (item?.status !== "paused" && item?.status !== "failed") return;
    this.update(key, { status: "queued", error: undefined });
    this.pump();
  }

  /**
   * Resumes an upload interrupted by a reload once the user picks the file
   * again. Returns false when the file isn't the one that was uploading.
   */
  reattach(key: string, file: File): boolean {
    const item = this.find(key);
    if (!item?.uploadId || item.status !== "interrupted") return false;
    const persisted = loadPersisted().find((r) => r.uploadId === item.uploadId);
    if (!persisted || !matchFile([persisted], file)) return false;
    this.internal.set(key, { file, meter: new SpeedMeter() });
    this.update(key, { status: "queued", error: undefined });
    this.pump();
    return true;
  }

  async cancel(key: string): Promise<void> {
    const item = this.find(key);
    if (!item || item.status === "done" || item.status === "canceled") return;
    this.internal.get(key)?.controller?.abort();
    this.update(key, { status: "canceled", rate: null });
    if (item.uploadId) {
      forget(item.uploadId);
      await this.deps.abortRemote(item.uploadId).catch(() => undefined);
    }
    this.pump();
  }

  /** Removes finished, canceled and failed rows from the panel. */
  clearFinished(): void {
    this.items = this.items.filter((i) => !["done", "canceled"].includes(i.status));
    this.emit();
  }

  dismiss(key: string): void {
    const item = this.find(key);
    if (item?.uploadId && item.status === "interrupted") forget(item.uploadId);
    this.items = this.items.filter((i) => i.key !== key);
    this.internal.delete(key);
    this.emit();
  }

  // --- internals --------------------------------------------------------------

  private addInterrupted(record: PersistedUpload): void {
    const key = String(this.nextKey++);
    this.internal.set(key, { meter: new SpeedMeter() });
    this.items.push({
      key,
      title: record.title,
      fileName: record.name,
      size: record.size,
      drm: record.drm,
      status: "interrupted",
      uploadedBytes: 0,
      rate: null,
      uploadId: record.uploadId,
      assetId: record.assetId,
    });
  }

  private pump(): void {
    const running = this.items.filter((i) => i.status === "uploading").length;
    const queued = this.items.filter((i) => i.status === "queued");
    for (const item of queued.slice(0, Math.max(0, PARALLEL_FILES - running))) {
      void this.run(item.key);
    }
  }

  private async run(key: string): Promise<void> {
    const item = this.find(key);
    const internal = this.internal.get(key);
    if (!item || !internal?.file) return;
    const file = internal.file;
    const controller = new AbortController();
    internal.controller = controller;
    internal.meter.reset();
    this.update(key, { status: "uploading" });

    const options: UploadClientOptions = {
      apiBase: "",
      getToken: this.deps.getToken,
      fetch: this.deps.fetch,
      concurrency: this.deps.concurrency,
      signal: controller.signal,
      onSession: (session) => {
        this.update(key, { uploadId: session.uploadId, assetId: session.assetId });
        persist({
          uploadId: session.uploadId,
          assetId: session.assetId,
          title: item.title,
          name: file.name,
          size: file.size,
          lastModified: file.lastModified,
          drm: item.drm,
          startedAt: Date.now(),
        });
      },
      onProgress: (progress) => {
        internal.meter.add(progress.uploadedBytes);
        this.update(key, { uploadedBytes: progress.uploadedBytes, rate: internal.meter.rate() });
      },
    };

    try {
      const current = this.find(key);
      const outcome = current?.uploadId
        ? await resumeUpload(options, current.uploadId, fileSource(file))
        : await uploadAsset(options, {
            source: fileSource(file),
            title: item.title,
            drm: item.drm,
          });
      forget(outcome.uploadId);
      this.update(key, { status: "done", uploadedBytes: item.size, rate: null });
      const done = this.find(key);
      if (done) this.deps.onComplete(done);
      setTimeout(() => {
        if (this.find(key)?.status === "done") this.dismiss(key);
      }, DONE_LINGER_MS);
    } catch (error) {
      if (error instanceof UploadClientError && error.code === "aborted") return; // paused or canceled
      this.update(key, { status: "failed", rate: null, error: describe(error) });
    } finally {
      internal.controller = undefined;
      this.pump();
    }
  }

  private find(key: string): UploadItem | undefined {
    return this.items.find((i) => i.key === key);
  }

  private update(key: string, patch: Partial<UploadItem>): void {
    this.items = this.items.map((i) => (i.key === key ? { ...i, ...patch } : i));
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}
